import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@rtkwlf/fenrir-react';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { faRotateBack } from '@rtkwlf/pro-solid-svg-icons/faRotateBack';
import { useQuery } from '@tanstack/react-query';

import {
  useGetOrganizationOptions,
  useGetOrganizationSecurityFocusesOptions,
} from '@apis/track-public/query-options';
import { useDocumentTitle } from '@packages/fenrir/hooks/use-document-title';
import { PageRootContent } from '@packages/fenrir/page-blocks/page/page-root';
import { GeneralWrapper } from 'ae-components/generalWrapper/GeneralWrapper';

import { ConciergeTierBadge } from '../../components/concierge-tier-badge/concierge-tier-badge';
import {
  Page,
  PageContent,
  PageHeader,
  PagePanel,
  PagePanelTitle,
  PageTitle,
} from '../../components/page/page';
import { WidgetError } from '../../components/widget/widget-error';
import { useSecurityJourneyTranslations } from '../../locales/use-security-journey-translations';

import { ActiveSecurityFocusTimeline } from './components/active-security-focus-timeline';
import { AllSecurityFocusesTable } from './components/all-security-focuses-table';
import styles from './page.module.css';

export const SecurityFocusesPage = () => {
  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Set the document title
   */
  useDocumentTitle(t('securityFocuses.documentTitle'));

  /**
   * fetch all security focuses
   */
  const {
    data: focuses,
    isLoading,
    isError,
    refetch,
  } = useQuery(useGetOrganizationSecurityFocusesOptions());

  /**
   * fetch the details of the organization
   */
  const { data: orgTier } = useQuery({
    ...useGetOrganizationOptions(),
    select: (data) => data.tierInfo,
  });

  return (
    <Page>
      <PageHeader>
        <div className={styles['page-title']}>
          <PageTitle>{t('securityFocuses.title')}</PageTitle>
          {orgTier && !orgTier.legacy && (
            <ConciergeTierBadge.Organization tier={orgTier.tier} />
          )}
        </div>
      </PageHeader>
      <PageContent>
        <ActiveSecurityFocusTimeline />
        <PagePanel>
          <PagePanelTitle>
            {t('securityFocuses.subtitleAllFocuses')}
          </PagePanelTitle>
          <GeneralWrapper
            marginTop="xlarge"
            isLoading={isLoading}
            isError={isError}
            loader={
              <Skeleton
                data-testid="security-focuses-loading"
                width="full"
                height="10rem"
                margin="zero"
              />
            }
            error={
              <WidgetError
                data-testid="security-focuses-error"
                actionIcon={<FontAwesomeIcon icon={faRotateBack} size="sm" />}
                actionText={t('common.retry')}
                heading={t('securityFocuses.error.title')}
                description={t('common.clickToRetry')}
                icon={
                  <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                }
                onActionClick={() => refetch()}
              />
            }
          >
            {focuses && <AllSecurityFocusesTable focuses={focuses} />}
          </GeneralWrapper>
        </PagePanel>
      </PageContent>
    </Page>
  );
};

export const Component = () => (
  <PageRootContent>
    <SecurityFocusesPage />
  </PageRootContent>
);
